import { useAuth } from "@/auth/auth";
import type { components } from "@/client/backend/zz_schema";
import { ShownOnceSecret } from "@/components/copy";
import { Alert } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Eye, EyeOff, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const path = "/v1/organizations/{organizationId}/instance_webhook";
type Configuration = components["schemas"]["InstanceWebhook"];
type Action = "save" | "delete" | "rotate" | "test";
const failureTitles: Record<Action, string> = {
  save: "Could not save webhook", delete: "Could not delete webhook",
  rotate: "Could not rotate signing secret", test: "Test failed",
};

export function InstanceWebhookSettings({ organizationId }: { organizationId: string }) {
  // Switching organizations discards drafts and credentials, including pending responses.
  return <WebhookForm key={organizationId} organizationId={organizationId} />;
}

function WebhookForm({ organizationId }: { organizationId: string }) {
  const { client } = useAuth();
  const mounted = useRef(false);
  const confirmationTrigger = useRef<HTMLButtonElement | null>(null);
  const heading = useRef<HTMLDivElement | null>(null);
  const errorToast = useRef<ReturnType<typeof toast> | null>(null);
  const [configuration, setConfiguration] = useState<Configuration | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Action | null>(null);
  const [confirm, setConfirm] = useState<"delete" | "rotate" | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [url, setURL] = useState("");
  const [authorization, setAuthorization] = useState("");
  const [showAuthorization, setShowAuthorization] = useState(false);
  const [removeAuthorization, setRemoveAuthorization] = useState(false);
  const [includeInstance, setIncludeInstance] = useState(false);
  const [signingSecret, setSigningSecret] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [testResult, setTestResult] = useState("");
  const params = { path: { organizationId } };
  const sameURL = url === configuration?.url;
  const dirty = url !== (configuration?.url ?? "") || authorization !== "" || removeAuthorization
    || includeInstance !== (configuration?.includeInstance ?? false);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    void (async () => {
      try {
        const r = await client.GET(path, { params: { path: { organizationId } }, signal: controller.signal });
        if (controller.signal.aborted) return;
        if (r.response.status === 403) {
          setForbidden(true);
        } else if (r.response.status !== 404) {
          if (r.response.status !== 200 || !r.data) throw new Error("Could not load webhook settings");
          setConfiguration(r.data);
          setURL(r.data.url);
          setIncludeInstance(r.data.includeInstance === true);
        }
        setLoading(false);
      } catch {
        if (!controller.signal.aborted) setError("Could not load webhook settings. Reload this page to try again.");
      }
    })();
    return () => { mounted.current = false; controller.abort(); errorToast.current?.dismiss(); };
  }, [client, organizationId]);

  const run = async (action: Action) => {
    errorToast.current?.dismiss();
    setBusy(action);
    setShowAuthorization(false);
    setError("");
    setStatus("");
    setTestResult("");
    const reportFailure = (message: string) => {
      const title = failureTitles[action];
      setError(`${title}. ${message}`);
      errorToast.current = toast({ variant: "destructive", title, description: message });
    };
    try {
      const r = action === "delete" ? await client.DELETE(path, { params })
        : action === "rotate" ? await client.POST("/v1/organizations/{organizationId}/instance_webhook/signing_secret", { params })
        : action === "test" ? await client.POST("/v1/organizations/{organizationId}/instance_webhook/test", { params })
        : await client.PUT(path, { params, body: { url, authorization: removeAuthorization ? "" : authorization || undefined, includeInstance } });
      if (!mounted.current) return;
      if (r.response.status === 403) {
        setForbidden(true);
        setAuthorization("");
        setSigningSecret("");
        setURL("");
        setConfiguration(null);
        setIncludeInstance(false);
        reportFailure("Only organization admins can manage webhooks.");
        return;
      }
      // Proxies can return plain text or no body, so the HTTP status determines failure.
      if (r.response.status < 200 || r.response.status >= 300) {
        const message = typeof r.error?.message === "string" ? r.error.message.trim() : "";
        reportFailure(message || "The server returned an error. Try again.");
        return;
      }
      if (action === "test") {
        const result = r.data as { delivered?: boolean; message?: string } | undefined;
        if (typeof result?.delivered !== "boolean" || typeof result.message !== "string" || !result.message.trim()) {
          reportFailure("The server did not return a test result. Try again.");
        } else if (result.delivered) {
          setTestResult(result.message);
        } else {
          reportFailure(result.message);
        }
        return;
      }
      if (action === "rotate") {
        const result = r.data as { signingSecret?: string } | undefined;
        if (typeof result?.signingSecret !== "string" || !result.signingSecret) {
          setSigningSecret("");
          reportFailure("The server did not return the new secret. Rotate it again before updating your receiver.");
          return;
        }
        setSigningSecret(result.signingSecret);
        setStatus("Signing secret rotated. Update your receiver with the new secret below.");
        return;
      }
      if (action === "delete") {
        setConfiguration(null);
        setURL("");
        setIncludeInstance(false);
        setSigningSecret("");
        setStatus("Webhook deleted.");
      } else {
        const saved = r.data as components["schemas"]["InstanceWebhookSecret"] | undefined;
        if (!saved || typeof saved.url !== "string" || !saved.url
          || typeof saved.hasAuthorization !== "boolean" || typeof saved.includeInstance !== "boolean"
          || (saved.signingSecret !== undefined && (typeof saved.signingSecret !== "string" || !saved.signingSecret))) {
          reportFailure("The server did not return the saved settings. Reload this page before trying again.");
          return;
        }
        const changedDestination = saved.url !== configuration?.url;
        setConfiguration({ url: saved.url, hasAuthorization: saved.hasAuthorization, includeInstance: saved.includeInstance });
        setURL(saved.url);
        setIncludeInstance(saved.includeInstance);
        // A retry can omit the secret after the first request saved it successfully.
        if (saved.signingSecret || changedDestination) setSigningSecret(saved.signingSecret ?? "");
        setStatus(saved.signingSecret ? "Webhook saved. Copy the new signing secret below."
          : changedDestination ? "Webhook saved. If you don't have its signing secret, rotate it below to get a new one."
          : "Webhook saved. Your signing secret has not changed.");
      }
      setAuthorization("");
      setRemoveAuthorization(false);
    } catch {
      if (mounted.current) reportFailure("Could not confirm the change. Check your connection and try again.");
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const discard = () => {
    setURL(configuration?.url ?? "");
    setIncludeInstance(configuration?.includeInstance ?? false);
    setAuthorization("");
    setShowAuthorization(false);
    setRemoveAuthorization(false);
    setError("");
    setStatus("");
    errorToast.current?.dismiss();
  };

  return (
    <Card variant="section" className="w-full max-w-2xl min-w-0 flex-1">
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle ref={heading} tabIndex={-1}>Instance webhooks</CardTitle>
          {!loading && !forbidden && <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={`size-1.5 rounded-full ${configuration ? "bg-success" : "bg-muted-foreground"}`} aria-hidden="true" />
            {configuration ? "Configured" : "Not configured"}
          </span>}
        </div>
        <CardDescription>Get lifecycle events for all instance types at your HTTPS endpoint.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {forbidden ? <p role="alert">Only organization admins can manage webhooks.</p>
          : loading ? error ? <Alert variant="destructive">{error}</Alert> : <p role="status" className="text-sm text-muted-foreground">Loading webhook settings…</p>
          : <>
            {error && <Alert variant="destructive">{error}</Alert>}
            {status && <p role="status" className="text-sm">{status}</p>}
            <form onSubmit={(e) => { e.preventDefault(); if (dirty && !busy) void run("save"); }}>
              <fieldset disabled={busy !== null} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="webhook-url">Webhook URL</Label>
                  <Input id="webhook-url" type="url" required maxLength={2048} value={url}
                    onChange={(e) => { setURL(e.target.value); setAuthorization(""); setShowAuthorization(false); setRemoveAuthorization(false); setIncludeInstance(false); }} placeholder="https://example.com/webhooks/limrun"
                    aria-describedby="webhook-url-help" />
                  <p id="webhook-url-help" className="text-xs text-muted-foreground">Public HTTPS endpoint on port 443. Events are sent as JSON.</p>
                  {configuration && !sameURL && <p className="text-sm">Changing the URL creates a new signing secret and clears saved authorization and instance access. Configure them for the new receiver.</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="webhook-authorization">Authorization (optional)</Label>
                  <div className="relative">
                    <Input id="webhook-authorization" type={showAuthorization ? "text" : "password"}
                      autoComplete="new-password" autoCapitalize="none" spellCheck={false} maxLength={4096}
                      value={authorization} disabled={removeAuthorization} className="pr-10 ph-no-capture"
                      onChange={(e) => setAuthorization(e.target.value)} placeholder={configuration?.hasAuthorization && sameURL ? "Saved · leave blank to keep" : "Bearer your-token"}
                      aria-describedby="webhook-authorization-help" />
                    <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 text-muted-foreground"
                      disabled={removeAuthorization} aria-label={showAuthorization ? "Hide authorization" : "Show authorization"}
                      aria-controls="webhook-authorization" aria-pressed={showAuthorization} onClick={() => setShowAuthorization((visible) => !visible)}>
                      {showAuthorization ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </Button>
                  </div>
                  <p id="webhook-authorization-help" className="text-xs text-muted-foreground">
                    {configuration?.hasAuthorization && sameURL
                      ? "An Authorization value is saved. Leave blank to keep it, or enter a replacement."
                      : "Sent in the Authorization header if your receiver requires it, for example Bearer your-token."}
                  </p>
                  {configuration?.hasAuthorization && sameURL && <div className="flex items-center gap-2 pt-1">
                    <Checkbox id="webhook-remove-authorization" checked={removeAuthorization}
                      onCheckedChange={(value) => { setRemoveAuthorization(value === true); setAuthorization(""); setShowAuthorization(false); }} />
                    <Label htmlFor="webhook-remove-authorization">Remove saved Authorization value</Label>
                  </div>}
                </div>
                <section className="space-y-4 border-t pt-6" aria-labelledby="webhook-events-heading">
                  <div className="space-y-1">
                    <h3 id="webhook-events-heading" className="text-sm font-semibold">Events</h3>
                    <p className="text-xs text-muted-foreground">All three events are sent for iOS, Android, Xcode, and Gradle instances.</p>
                  </div>
                  <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
                    <dt><code className="text-xs">instance.created</code></dt><dd className="text-muted-foreground">An instance has been created.</dd>
                    <dt><code className="text-xs">instance.ready</code></dt><dd className="text-muted-foreground">An instance is ready to use.</dd>
                    <dt><code className="text-xs">instance.terminated</code></dt><dd className="text-muted-foreground">An instance has stopped, with its termination reason.</dd>
                  </dl>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Checkbox id="webhook-include-instance" className="mt-0.5" checked={includeInstance}
                        aria-describedby="webhook-include-instance-help" onCheckedChange={(value) => setIncludeInstance(value === true)} />
                      <Label htmlFor="webhook-include-instance" className="leading-5">Include instance details and access token in ready events</Label>
                    </div>
                    <p id="webhook-include-instance-help" className="pl-6 text-xs leading-5 text-muted-foreground">
                      Lets your app connect without another API call. The token grants control of that instance.
                      Only enable this for a receiver you trust. Changing the URL turns this off.
                    </p>
                  </div>
                </section>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="submit" disabled={!dirty}>{busy === "save" ? "Saving…" : "Save webhook"}</Button>
                  {dirty && <Button type="button" variant="ghost" onClick={discard}>Discard changes</Button>}
                </div>
              </fieldset>
            </form>
            <section className="space-y-3 border-t pt-6" aria-labelledby="webhook-signing-heading">
              <h3 id="webhook-signing-heading" className="text-sm font-semibold">Signing secret</h3>
              <p className="text-sm text-muted-foreground">Your receiver uses this secret to verify that events came from Limrun.</p>
              {signingSecret ? <div className="space-y-3 ph-no-capture">
                <p className="text-xs text-muted-foreground">Copy this secret now. It will not be shown again after you leave this page.</p>
                <ShownOnceSecret value={signingSecret} label="Copy signing secret" />
                <Button type="button" variant="outline" size="sm" onClick={() => setSigningSecret("")}>Hide secret</Button>
              </div> : <p className="text-xs text-muted-foreground">{configuration ? "A secret is saved. Ordinary edits keep it unchanged." : "A signing secret will be generated when you save your webhook."}</p>}
              {configuration && <Button type="button" variant="outline" disabled={!!busy || dirty} onClick={(e) => { confirmationTrigger.current = e.currentTarget; setConfirm("rotate"); }}>{busy === "rotate" ? "Rotating…" : "Rotate secret"}</Button>}
            </section>
            {configuration && <>
              <section className="space-y-3 border-t pt-6" aria-labelledby="webhook-test-heading">
                <h3 id="webhook-test-heading" className="text-sm font-semibold">Test your endpoint</h3>
                <p className="text-sm text-muted-foreground">Send a signed <code className="text-xs">webhook.ping</code> to your saved URL. No instance data is included.</p>
                <Button type="button" variant="outline" disabled={!!busy || dirty} onClick={() => void run("test")}><Send aria-hidden="true" />{busy === "test" ? "Sending…" : "Send test event"}</Button>
                <p className="text-xs text-muted-foreground">{dirty ? "Save or discard your changes before testing or rotating the secret." : "Makes one attempt and waits up to 20 seconds for a response."}</p>
                {testResult && <p role="status" className="text-sm text-success">{testResult}</p>}
              </section>
              <section className="flex flex-wrap items-center justify-between gap-3 border-t pt-6" aria-label="Delete webhook">
                <p className="text-sm text-muted-foreground">Remove this endpoint and its saved credentials.</p>
                <Button type="button" variant="danger-ghost" disabled={!!busy} onClick={(e) => { confirmationTrigger.current = e.currentTarget; setConfirm("delete"); }}>{busy === "delete" ? "Deleting…" : "Delete webhook"}</Button>
              </section>
            </>}
            <AlertDialog open={confirm !== null} onOpenChange={(open) => { if (!open) setConfirm(null); }}>
              <AlertDialogContent onCloseAutoFocus={(e) => {
                e.preventDefault();
                const trigger = confirmationTrigger.current;
                (trigger?.isConnected && !trigger.disabled ? trigger : heading.current)?.focus();
              }}>
                <AlertDialogHeader>
                  <AlertDialogTitle>{confirm === "delete" ? "Delete webhook?" : "Rotate signing secret?"}</AlertDialogTitle>
                  <AlertDialogDescription>{confirm === "delete"
                    ? "New events will no longer be sent to this endpoint. Its URL, authorization, and signing secret will be removed. Deliveries already in progress may finish."
                    : "New deliveries will use a new signing secret. Update your receiver immediately after rotating. Deliveries already in progress may still use the previous secret."}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className={buttonVariants({ variant: confirm === "delete" ? "destructive" : "default" })} onClick={() => { if (confirm) void run(confirm); }}>{confirm === "delete" ? "Delete webhook" : "Rotate secret"}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>}
      </CardContent>
    </Card>
  );
}
